// Pure helpers for the Rate Card base-pricing upload. No React, no side effects.
// Builds the profiling-scoped template model — one tab per parent × service type
// in the 17-column ops format — generates/parses the workbook, and produces the
// apply payload (rate-card rows + extracted rates + per-parent T&C overrides).
import * as XLSX from 'xlsx';
import { normalizeStCode } from '../constants';
import {
  ZONES, slabsForTemplate, SLAB_TEMPLATES, RVP_SLAB_PATTERNS,
  resolveTcForParent, computeRvp, computeModeARvp, classifyServiceLane, selectedCodesForService,
  blendedForwardLanes, blendedLaneAssignments,
} from './rateEngine';

// Excel sheet names: max 31 chars, no  : \ / ? * [ ]
// The PARENT gets trimmed (label kept whole); collisions get ~2, ~3 … suffixes.
const SHEET_NAME_MAX = 31;
function sanitizeSheetPart(s) {
  return String(s || '').replace(/[:\\/?*[\]]/g, ' ').replace(/\s+/g, ' ').trim();
}

export function sheetNameForCombo(parent, label, used) {
  const lbl = sanitizeSheetPart(label) || 'Sheet';
  let par = sanitizeSheetPart(parent) || 'Parent';
  let base = `${par}-${lbl}`;
  if (base.length > SHEET_NAME_MAX) {
    const room = Math.max(SHEET_NAME_MAX - lbl.length - 1, 3);
    par = `${par.slice(0, room - 1)}…`;
    base = `${par}-${lbl}`.slice(0, SHEET_NAME_MAX);
  }
  let name = base;
  let n = 2;
  while (used.has(name)) {
    const sfx = `~${n}`;
    name = base.slice(0, SHEET_NAME_MAX - sfx.length) + sfx;
    n += 1;
  }
  used.add(name);
  return name;
}

// The 17 ops-CSV headers, verbatim — generate and parse both key off these.
export const TEMPLATE_HEADERS = [
  'Zone', 'Start Weight(gm)', 'Min Weight(gm)', 'Max Weight(gm)', 'Price',
  'Additional Unit(gm)', 'Additional Unit Rate', 'Volumetric Coefficient', 'Tax(%)',
  'Fuel Surcharge(%)', 'Docket Charge', 'Invoice Percentage for COD(Optional)%',
  'COD Operator(Min/Max)(Optional)', 'Fixed COD Charge(Optional)', 'RVP Without QC',
  'RVP With QC', 'RTO (Forward %)',
];

// ── Combo model: one entry per (parent × resolved service type) ─────────────
// Forward services always; reverse (REV_*) only when RVP is enabled in
// Independent mode — mirrors the wizard's separate RVP rate card.
// Blended rate card: one sheet per forward LANE (Surface / Air / SDD-NDD),
// parents collapsed. The single price set is fanned onto every selected account
// code at upload time. No reverse tabs and no hidden __META__ sheet — the file
// is exactly the lane sheets.
function buildBlendedTemplateModel(wizard) {
  const used = new Set();
  const combos = blendedForwardLanes(wizard).map((lane) => {
    const assigns = blendedLaneAssignments(wizard, lane.id);
    const repParent = (assigns[0] && assigns[0].parent) || lane.label;
    const repService = (assigns[0] && assigns[0].service) || lane.id;
    let name = sanitizeSheetPart(lane.label).slice(0, SHEET_NAME_MAX) || 'Sheet';
    let n = 2;
    while (used.has(name)) { name = `${lane.label} ${n}`.slice(0, SHEET_NAME_MAX); n += 1; }
    used.add(name);
    return {
      parent: repParent,
      serviceId: repService,
      kind: 'forward',
      label: lane.label,
      sheetName: name,
      tc: resolveTcForParent(wizard, repParent, repService),
      blended: true,
      lane: lane.id,
    };
  });
  return {
    combos,
    zones: ZONES,
    slabs: slabsForTemplate(wizard),
    rvpSlabs: slabsForTemplate(wizard),
    vc: (wizard.agreement && wizard.agreement.vc) != null ? wizard.agreement.vc : '',
    slabTemplateLabel: (SLAB_TEMPLATES.find((t) => t.id === wizard.rateCard.slabTemplate) || {}).label,
  };
}

export function buildTemplateModel(wizard) {
  if (wizard.rateCardMode === 'blended') return buildBlendedTemplateModel(wizard);
  const resolved = wizard.resolvedServices || [];
  const groups = wizard.serviceabilityGroups || [];
  // Reverse services are templated whenever RVP is enabled (Mode A or B).
  const wantReverse = !!(wizard.rvp && wizard.rvp.enabled);
  // Mode B → reverse sheets are kind 'rvp' (separate RVP download). Mode A → reverse
  // couriers live in the SAME (forward) sheet, so they're kind 'forward' (their RVP
  // columns are formula-filled, forward-price columns blank).
  const reverseKind = (wizard.rvp && wizard.rvp.mode === 'independent') ? 'rvp' : 'forward';
  const used = new Set(['__META__']);
  const combos = [];
  resolved.forEach((s) => {
    if (!s || !s.id) return;
    const rev = String(s.id).toUpperCase().startsWith('REV_');
    if (rev && !wantReverse) return;
    const matching = groups.filter((g) => normalizeStCode(g.code) === normalizeStCode(s.id));
    const activeCodes = matching.flatMap((g) => g.linkedCodes || []).filter((lc) => lc.isActive !== false);
    // Honour the per-service account-code selection from the Profile step.
    const allCodeStrs = activeCodes.map((lc) => lc.cpAccountCode);
    const selectedSet = new Set(selectedCodesForService(wizard.accountCodeSelection, s.id, allCodeStrs));
    // Tab label = the raw service-type id (ops convention: ATS-FWD_SURFACE_REG).
    const label = String(s.id);

    // One sheet per PARENT courier × service type — a parent with several selected
    // account codes gets a single tab (its rate fans out to all its codes on upload).
    const parents = [];
    activeCodes.forEach((lc) => {
      if (!selectedSet.has(lc.cpAccountCode)) return;
      const p = lc.parent || lc.cpAccountCode;
      if (p && !parents.includes(p)) parents.push(p);
    });
    parents.forEach((parent) => {
      combos.push({
        parent, serviceId: s.id, kind: rev ? reverseKind : 'forward', label,
        sheetName: sheetNameForCombo(parent, label, used),
        tc: resolveTcForParent(wizard, parent, s.id),
      });
    });
  });
  const rvpPattern = RVP_SLAB_PATTERNS.find((p) => p.id === (wizard.rvp && wizard.rvp.slabPattern));
  return {
    combos,
    zones: ZONES,
    slabs: slabsForTemplate(wizard),
    rvpSlabs: (rvpPattern && rvpPattern.slabs) || slabsForTemplate(wizard),
    vc: (wizard.agreement && wizard.agreement.vc) != null ? wizard.agreement.vc : '',
    slabTemplateLabel: (SLAB_TEMPLATES.find((t) => t.id === wizard.rateCard.slabTemplate) || {}).label,
  };
}

// ── Apply payload: parsed combos → rate-card rows + extracted rates ─────────
// The uploaded file is the source of truth: every file row maps 1:1 onto a
// rate-card row per account code; row-level T&C wins; FSC/COD/RTO deviations
// from the wizard defaults are surfaced as per-parent overrides.
function accountCodesFor(wizard, parent, serviceId) {
  const codes = new Set();
  (wizard.serviceabilityGroups || [])
    .filter((g) => String(g.code || '').toUpperCase() === String(serviceId).toUpperCase())
    .forEach((g) => {
      (g.linkedCodes || []).filter((lc) => lc.isActive !== false).forEach((lc) => {
        if ((lc.parent || lc.cpAccountCode) === parent) codes.add(lc.cpAccountCode);
      });
    });
  if (!codes.size) return [parent]; // no real serviceability codes — placeholder
  // Restrict to the codes selected for this service on the Profile step.
  const sel = wizard.accountCodeSelection && wizard.accountCodeSelection[serviceId];
  if (sel) {
    const selSet = new Set(sel);
    const filtered = [...codes].filter((c) => selSet.has(c));
    if (filtered.length) return filtered;
  }
  return [...codes];
}

export function buildApplyPayload(parsed, wizard) {
  const slabKeys = slabsForTemplate(wizard).map(([a, b]) => `${a}-${b}`);
  const inclusive = !!(wizard.agreement && wizard.agreement.gst_stance === 'inclusive');
  const exDiv = (v) => (v == null ? v : +(v / 1.18).toFixed(2));

  const rows = [];
  const forwardRates = {};               // parent → zone → slabKey (raw prices)
  const laneRank = { surface: 0, air: 1, sdd_ndd: 2 };
  const storedLane = {};                 // parent → rank of the lane currently stored
  const rvpRates = {};
  const rvpRows = [];                    // full reverse rows (forward column shape) for Mode B
  let rvpSlabKeys = null;
  const perCourierTc = {};
  // Mode A: RVP nQC/QC come from the per-courier formula applied to the forward price
  // (max(FWD + ₹X, FWD × M) + QC charge), not the uploaded RVP cells.
  const rvpModeA = !!(wizard.rvp && wizard.rvp.enabled && wizard.rvp.mode === 'forward_linked');
  const modeARvp = (parent, fwd) => {
    const fm = (wizard.rvp && wizard.rvp.formulas) || {};
    const f = wizard.rateCardMode === 'blended' ? fm.__blended__ : (fm[parent] || fm.__blended__);
    return computeModeARvp(f || {}, fwd);
  };

  (parsed.combos || []).forEach((combo) => {
    const tcr = resolveTcForParent(wizard, combo.parent, combo.serviceId);

    // Blended lane: one price set fans out onto every selected (parent, account,
    // service) tuple in the lane, and every parent gets the same extracted rates.
    if (combo.blended) {
      const assigns = blendedLaneAssignments(wizard, combo.lane);
      const parentsInLane = [...new Set(assigns.map((a) => a.parent))];
      const hasPrice = combo.rows.some((r) => r.price != null);
      if (hasPrice) parentsInLane.forEach((p) => { forwardRates[p] = {}; });
      combo.rows.forEach((r) => {
        let rvpNo = r.rvp_noqc;
        let rvpQc = r.rvp_qc;
        if (rvpModeA) {
          const m = modeARvp(parentsInLane[0], r.price);
          rvpNo = m.rvp_noqc; rvpQc = m.rvp_qc;
        } else if (rvpNo == null && r.price != null) {
          const c = computeRvp({ rvp_formula: tcr.rvp_formula, rvp_flat: tcr.rvp_flat, rvp_pct: tcr.rvp_pct, qc: tcr.qc }, r.price);
          rvpNo = c.rvp_noqc;
          rvpQc = rvpQc == null ? c.rvp_qc : rvpQc;
        } else if (rvpNo != null && rvpQc == null) {
          rvpQc = +(rvpNo + tcr.qc).toFixed(2);
        }
        if (hasPrice && r.price != null) {
          parentsInLane.forEach((p) => {
            if (!forwardRates[p][r.zone]) forwardRates[p][r.zone] = {};
            forwardRates[p][r.zone][`${r.min}-${r.max}`] = r.price;
          });
        }
        const money = inclusive
          ? { price: exDiv(r.price), rvp_noqc: exDiv(rvpNo), rvp_qc: exDiv(rvpQc), price_incl: r.price, rvp_noqc_incl: rvpNo, rvp_qc_incl: rvpQc }
          : { price: r.price, rvp_noqc: rvpNo, rvp_qc: rvpQc };
        assigns.forEach(({ parent, account_code, service }) => {
          const ar = (wizard.arrangement || {})[service] || { type: 'ZWZ', mode: 'blended' };
          rows.push({
            account_code, parent, service,
            arrangement: ar.type, mode: ar.mode || 'blended',
            zone: r.zone, start: r.start, min: r.min, max: r.max,
            addl_unit: r.addl_unit, addl_rate: r.addl_rate,
            vc: r.vc != null ? r.vc : (wizard.agreement && wizard.agreement.vc),
            tax: r.tax, fsc: r.fsc, docket: r.docket,
            cod_pct: r.cod_pct, cod_op: r.cod_op, cod_fixed: r.cod_fixed,
            cod_charge_type: r.cod_charge_type || 'FIXED',
            ...(r.cod_slabs && r.cod_slabs.length ? { cod_slabs: r.cod_slabs } : {}),
            rto_pct: r.rto_pct,
            tc_source: 'template upload',
            ...money,
          });
        });
      });
      return;
    }

    if (combo.kind === 'rvp') {
      combo.rows.forEach((r) => {
        if (r.price == null && r.rvp_noqc == null && r.rvp_qc == null) return;
        if (r.price != null) {
          if (!rvpRates[combo.parent]) rvpRates[combo.parent] = {};
          if (!rvpRates[combo.parent][r.zone]) rvpRates[combo.parent][r.zone] = {};
          rvpRates[combo.parent][r.zone][`${r.min}-${r.max}`] = r.price;
        }
        // Fill blank RVP cells from the parent formula on the raw price (same as forward).
        let rvpNo = r.rvp_noqc;
        let rvpQc = r.rvp_qc;
        if (rvpNo == null && r.price != null) {
          const c = computeRvp({ rvp_formula: tcr.rvp_formula, rvp_flat: tcr.rvp_flat, rvp_pct: tcr.rvp_pct, qc: tcr.qc }, r.price);
          rvpNo = c.rvp_noqc;
          rvpQc = rvpQc == null ? c.rvp_qc : rvpQc;
        } else if (rvpNo != null && rvpQc == null) {
          rvpQc = +(rvpNo + tcr.qc).toFixed(2);
        }
        const money = inclusive
          ? { price: exDiv(r.price), rvp_noqc: exDiv(rvpNo), rvp_qc: exDiv(rvpQc), price_incl: r.price, rvp_noqc_incl: rvpNo, rvp_qc_incl: rvpQc }
          : { price: r.price, rvp_noqc: rvpNo, rvp_qc: rvpQc };
        // Reverse rows carry the full forward column shape so the Mode B card can
        // render the same matrix — per account code (falls back to parent for legacy
        // per-parent sheets).
        rvpRows.push({
          account_code: combo.accountCode || combo.parent, parent: combo.parent, service: combo.serviceId,
          arrangement: 'ZWZ', mode: 'cp',
          zone: r.zone, start: r.start, min: r.min, max: r.max,
          addl_unit: r.addl_unit, addl_rate: r.addl_rate,
          vc: r.vc != null ? r.vc : (wizard.agreement && wizard.agreement.vc),
          tax: r.tax, fsc: r.fsc, docket: r.docket,
          cod_pct: r.cod_pct, cod_op: r.cod_op, cod_fixed: r.cod_fixed,
          cod_charge_type: r.cod_charge_type || 'FIXED',
          ...(r.cod_slabs && r.cod_slabs.length ? { cod_slabs: r.cod_slabs } : {}),
          rto_pct: r.rto_pct,
          tc_source: 'template upload', ...money,
        });
      });
      if (!rvpSlabKeys && combo.rows.length) {
        rvpSlabKeys = [...new Set(combo.rows.map((r) => `${r.min}-${r.max}`))];
      }
      return;
    }

    // forward — per-parent override detection from the first row. A parent with
    // several forward tabs merges patches last-tab-wins (tc.perCourier is
    // parent-keyed; per-service T&C still lives on each rate-card row).
    const first = combo.rows[0];
    if (first) {
      const patch = {};
      if (first.fsc !== tcr.fsc) patch.fsc = first.fsc;
      if (first.cod_pct !== tcr.cod_pct) patch.cod_pct = first.cod_pct;
      if (String(first.cod_op).toUpperCase() !== String(tcr.cod_op || 'MAX').toUpperCase()) patch.cod_op = first.cod_op;
      if (first.cod_fixed !== tcr.cod_fixed) patch.cod_fixed = first.cod_fixed;
      if (first.rto_pct !== tcr.rto_pct) patch.rto_pct = first.rto_pct;
      if (Object.keys(patch).length) {
        perCourierTc[combo.parent] = { ...(perCourierTc[combo.parent] || {}), ...patch };
      }
    }

    const lane = classifyServiceLane(combo.serviceId);
    const rank = laneRank[lane] != null ? laneRank[lane] : 3;
    // A tab claims the parent's extracted-rates slot only if it has at least one
    // price — a blank preferred-lane tab must not wipe a filled lower-lane one.
    // Same-rank ties (e.g. Surface REG + LARGE): first tab in profile order wins.
    const hasPrice = combo.rows.some((r) => r.price != null);
    const takeRates = hasPrice && (storedLane[combo.parent] == null || rank < storedLane[combo.parent]);
    if (takeRates) { forwardRates[combo.parent] = {}; storedLane[combo.parent] = rank; }

    const ar = (wizard.arrangement || {})[combo.serviceId] || { type: 'ZWZ', mode: 'cp' };
    // Per-account-code sheet → apply to that code only. Legacy per-parent sheets (no
    // accountCode) still fan out to every selected code under the parent.
    const codes = combo.accountCode ? [combo.accountCode] : accountCodesFor(wizard, combo.parent, combo.serviceId);

    combo.rows.forEach((r) => {
      // Mode A → RVP from the per-courier formula (overrides any uploaded RVP cells);
      // otherwise blank RVP cells are computed from the T&C formula on RAW prices.
      let rvpNo = r.rvp_noqc;
      let rvpQc = r.rvp_qc;
      if (rvpModeA) {
        const m = modeARvp(combo.parent, r.price);
        rvpNo = m.rvp_noqc; rvpQc = m.rvp_qc;
      } else if (rvpNo == null && r.price != null) {
        const c = computeRvp({ rvp_formula: tcr.rvp_formula, rvp_flat: tcr.rvp_flat, rvp_pct: tcr.rvp_pct, qc: tcr.qc }, r.price);
        rvpNo = c.rvp_noqc;
        rvpQc = rvpQc == null ? c.rvp_qc : rvpQc;
      } else if (rvpNo != null && rvpQc == null) {
        rvpQc = +(rvpNo + tcr.qc).toFixed(2);
      }

      if (takeRates && r.price != null) {
        if (!forwardRates[combo.parent][r.zone]) forwardRates[combo.parent][r.zone] = {};
        forwardRates[combo.parent][r.zone][`${r.min}-${r.max}`] = r.price; // raw, engine convention
      }

      const money = inclusive
        ? { price: exDiv(r.price), rvp_noqc: exDiv(rvpNo), rvp_qc: exDiv(rvpQc), price_incl: r.price, rvp_noqc_incl: rvpNo, rvp_qc_incl: rvpQc }
        : { price: r.price, rvp_noqc: rvpNo, rvp_qc: rvpQc };

      codes.forEach((acc) => {
        rows.push({
          account_code: acc, parent: combo.parent, service: combo.serviceId,
          arrangement: ar.type, mode: ar.mode,
          zone: r.zone, start: r.start, min: r.min, max: r.max,
          addl_unit: r.addl_unit, addl_rate: r.addl_rate,
          vc: r.vc != null ? r.vc : (wizard.agreement && wizard.agreement.vc),
          tax: r.tax, fsc: r.fsc, docket: r.docket,
          cod_pct: r.cod_pct, cod_op: r.cod_op, cod_fixed: r.cod_fixed,
          cod_charge_type: r.cod_charge_type || 'FIXED',
          ...(r.cod_slabs && r.cod_slabs.length ? { cod_slabs: r.cod_slabs } : {}),
          rto_pct: r.rto_pct,
          tc_source: 'template upload',
          ...money,
        });
      });
    });
  });

  return {
    rows,
    // Every (parent|service) the upload actually wrote — the apply step replaces
    // exactly these and keeps the rest. For blended this spans all lane parents.
    covered: [...new Set(rows.map((r) => `${r.parent}|${r.service}`))],
    forwardExtracted: { rates: forwardRates, slabKeys },
    rvpExtracted: { rates: rvpRates, rows: rvpRows, slabKeys: rvpSlabKeys || slabKeys, source: 'template upload' },
    perCourierTc,
  };
}

// ── CSV helpers — tab-per-section format ─────────────────────────────────────
// Mirrors the Excel multi-sheet layout: each parent × service-type combo is a
// "tab" prefixed by `# SheetName` (same name used in the workbook).
// Parser reconstructs combos by matching those section headers.

function escCsv(v) {
  if (v == null || v === '') return '';
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function generateTemplateCsv(model) {
  const sections = [];

  model.combos.forEach((combo) => {
    const slabs = combo.kind === 'rvp' ? model.rvpSlabs : model.slabs;
    const lines = [];

    // Section header — same format as the Excel sheet name
    lines.push(`# ${combo.sheetName}`);

    // Column headers (same 17 cols as Excel — no Parent/Service Type needed)
    lines.push(TEMPLATE_HEADERS.map(escCsv).join(','));

    // Data rows: zone × slab, pre-filled defaults, Price left blank
    model.zones.forEach((z) => {
      slabs.forEach(([min, max]) => {
        lines.push([
          z.id, min, min, max, '',
          0, 0, model.vc != null ? model.vc : 5000, 18,
          combo.tc.fsc, 0, combo.tc.cod_pct,
          combo.tc.cod_op, combo.tc.cod_fixed, '', '', combo.tc.rto_pct,
        ].map(escCsv).join(','));
      });
    });

    sections.push(lines.join('\n'));
  });

  return sections.join('\n\n'); // blank line between tabs
}

// Parse the tab-per-section CSV back into the combo model.
// Fallback: also handles the old flat format (Parent + Service Type columns).
export function parseFlatCsvToModel(text, wizard) {
  const expectedCombos = buildTemplateModel(wizard).combos;
  const sheetToCombo = new Map(
    expectedCombos.map((c) => [c.sheetName.toLowerCase(), c]),
  );

  const lines = text.split(/\r?\n/);
  const combos = [];
  const missingSheets = [];
  const unknownSections = [];
  let badPriceCells = 0;

  // ── Tab-per-section parsing ──
  let currentCombo = null;
  let currentCol = null;

  const flushSection = () => { currentCombo = null; currentCol = null; };

  const parseDataRow = (row) => {
    if (!currentCombo || !currentCol) return;
    const cell = (h) => (currentCol[h] >= 0 ? row[currentCol[h]] : '');
    const rawPrice = cell('Price');
    const price = num(rawPrice);
    if (price == null && rawPrice != null && String(rawPrice).trim() !== '') badPriceCells += 1;
    const minVal = nz(num(cell('Min Weight(gm)')), 0);
    const zone = num(cell('Zone'));
    const max = num(cell('Max Weight(gm)'));
    if (zone == null || max == null) return;
    currentCombo.rows.push({
      zone, start: nz(num(cell('Start Weight(gm)')), minVal), min: minVal, max, price,
      addl_unit: nz(num(cell('Additional Unit(gm)')), 0),
      addl_rate: nz(num(cell('Additional Unit Rate')), 0),
      vc: num(cell('Volumetric Coefficient')),
      tax: nz(num(cell('Tax(%)')), 18),
      fsc: nz(num(cell('Fuel Surcharge(%)')), 0),
      docket: nz(num(cell('Docket Charge')), 0),
      cod_pct: nz(num(cell('Invoice Percentage for COD(Optional)%')), 0),
      cod_op: String(cell('COD Operator(Min/Max)(Optional)') || 'MAX').trim().toUpperCase() || 'MAX',
      cod_fixed: nz(num(cell('Fixed COD Charge(Optional)')), 0),
      rvp_noqc: num(cell('RVP Without QC')),
      rvp_qc: num(cell('RVP With QC')),
      rto_pct: nz(num(cell('RTO (Forward %)')), 0),
    });
  };

  // Detect format: tab-per-section vs flat
  const isTabFormat = lines.some((l) => l.trim().startsWith('#'));

  if (isTabFormat) {
    lines.forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) return; // blank separator between sections

      if (trimmed.startsWith('#')) {
        // Section header: `# SheetName`
        const sheetName = trimmed.replace(/^#+\s*/, '').trim();
        const meta = sheetToCombo.get(sheetName.toLowerCase());
        if (meta) {
          currentCombo = { parent: meta.parent, serviceId: meta.serviceId, kind: meta.kind, rows: [] };
          combos.push(currentCombo);
          currentCol = null; // next non-blank line is headers
        } else {
          unknownSections.push(sheetName);
          flushSection();
        }
        return;
      }

      if (!currentCombo) return;

      const cells = trimmed.split(',').map((c) => c.replace(/^"(.*)"$/, '$1').replace(/""/g, '"'));

      if (!currentCol) {
        // First non-blank line after section header = column headers
        currentCol = {};
        TEMPLATE_HEADERS.forEach((h) => { currentCol[h] = cells.map((c) => c.trim()).indexOf(h); });
        return;
      }

      parseDataRow(cells);
    });

    // Check for missing expected sheets
    const parsedNames = new Set(combos.map((c) => `${c.parent}||${c.serviceId}`));
    expectedCombos.forEach((c) => {
      if (!parsedNames.has(`${c.parent}||${c.serviceId}`)) missingSheets.push(c.sheetName);
    });
  } else {
    // Flat format fallback (Parent + Service Type columns)
    const wb = XLSX.read(text, { type: 'string' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    const header = (rows[0] || []).map((h) => String(h).trim());
    const pCol = header.indexOf('Parent');
    const sCol = header.indexOf('Service Type');
    if (pCol < 0 || sCol < 0) {
      return { combos: [], missingSheets: [], badHeaderSheets: ['CSV: missing section headers or Parent/Service Type columns'], unknownSheets: [], badPriceCells: 0, metaSlabTemplate: null };
    }
    const col = {};
    TEMPLATE_HEADERS.forEach((h) => { col[h] = header.indexOf(h); });
    const flatMap = new Map();
    rows.slice(1).forEach((row) => {
      const parent = String(row[pCol] || '').trim();
      const serviceId = String(row[sCol] || '').trim();
      if (!parent || !serviceId) return;
      const key = `${parent}||${serviceId}`;
      if (!flatMap.has(key)) {
        flatMap.set(key, { parent, serviceId, kind: serviceId.toUpperCase().startsWith('REV_') ? 'rvp' : 'forward', rows: [] });
      }
      currentCombo = flatMap.get(key);
      currentCol = col;
      parseDataRow(row);
    });
    combos.push(...flatMap.values());
  }

  return { combos, missingSheets, badHeaderSheets: [], unknownSheets: unknownSections, badPriceCells, metaSlabTemplate: null };
}

// ── Workbook generation: one 17-column sheet per combo ──────────────────────
// Pre-fill: structure (zone × slab ladder) + known defaults (VC, Tax 18, FSC,
// COD, RTO from the combo's resolved T&C). Price and RVP cells stay blank.
function comboSheetAOA(model, combo) {
  const slabs = combo.kind === 'rvp' ? model.rvpSlabs : model.slabs;
  const aoa = [TEMPLATE_HEADERS.slice()];
  model.zones.forEach((z) => {
    slabs.forEach(([min, max]) => {
      aoa.push([
        z.id, min, min, max, '',
        0, 0, model.vc, 18,
        combo.tc.fsc, 0, combo.tc.cod_pct,
        combo.tc.cod_op, combo.tc.cod_fixed, '', '', combo.tc.rto_pct,
      ]);
    });
  });
  return aoa;
}

export function generateTemplateWorkbook(model, meta) {
  const wb = XLSX.utils.book_new();
  // Blended templates are exactly the lane sheets — no hidden __META__. The
  // upload path reconstructs the lane combos from the live wizard instead.
  const isBlended = (model.combos || []).some((c) => c.blended);
  if (!isBlended) {
    const metaJson = JSON.stringify({
      merchantId: (meta && meta.merchantId) != null ? meta.merchantId : null,
      slabTemplate: (meta && meta.slabTemplate) != null ? meta.slabTemplate : null,
      combos: model.combos.map(({ sheetName, parent, accountCode, serviceId, kind }) => ({ sheetName, parent, accountCode, serviceId, kind })),
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([['__META__'], [metaJson]]), '__META__');
  }
  model.combos.forEach((combo) => {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(comboSheetAOA(model, combo)), combo.sheetName);
  });
  wb.Workbook = { Sheets: wb.SheetNames.map((n) => ({ Hidden: n === '__META__' ? 1 : 0 })) };
  return wb;
}

// ── Parse: workbook buffer → { combos: [{parent, serviceId, kind, rows}] } ──
const num = (v) => {
  if (v == null || String(v).trim() === '') return null; // whitespace-only = blank
  return isNaN(Number(v)) ? null : Number(v);
};
const nz = (v, fallback) => (v == null ? fallback : v);

function readMeta(wb) {
  const ws = wb.Sheets.__META__;
  if (!ws) return null;
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  try {
    const meta = JSON.parse(rows[1] && rows[1][0]);
    const combos = (Array.isArray(meta.combos) ? meta.combos : [])
      .filter((c) => c && c.sheetName && c.parent && c.serviceId && c.kind);
    return combos.length ? { ...meta, combos } : null;
  } catch (e) {
    return null;
  }
}

function parseComboSheet(ws) {
  const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  const header = (rows[0] || []).map((h) => String(h).trim());
  const col = {};
  TEMPLATE_HEADERS.forEach((h) => { col[h] = header.indexOf(h); });
  if (col.Zone < 0 || col['Max Weight(gm)'] < 0 || col.Price < 0) {
    return { rows: [], badHeader: true, badPriceCells: 0 };
  }
  const cell = (row, h) => (col[h] < 0 ? '' : row[col[h]]);
  const out = [];
  let badPriceCells = 0;
  rows.slice(1).forEach((row) => {
    const zone = num(cell(row, 'Zone'));
    const max = num(cell(row, 'Max Weight(gm)'));
    if (zone == null || max == null) return;
    const rawPrice = cell(row, 'Price');
    const price = num(rawPrice);
    if (price == null && rawPrice != null && String(rawPrice).trim() !== '') badPriceCells += 1;
    const min = nz(num(cell(row, 'Min Weight(gm)')), 0);
    out.push({
      zone,
      start: nz(num(cell(row, 'Start Weight(gm)')), min),
      min,
      max,
      price,
      addl_unit: nz(num(cell(row, 'Additional Unit(gm)')), 0),
      addl_rate: nz(num(cell(row, 'Additional Unit Rate')), 0),
      vc: num(cell(row, 'Volumetric Coefficient')),
      tax: nz(num(cell(row, 'Tax(%)')), 18),
      fsc: nz(num(cell(row, 'Fuel Surcharge(%)')), 0),
      docket: nz(num(cell(row, 'Docket Charge')), 0),
      cod_pct: nz(num(cell(row, 'Invoice Percentage for COD(Optional)%')), 0),
      cod_op: String(cell(row, 'COD Operator(Min/Max)(Optional)') || 'MAX').trim().toUpperCase() || 'MAX',
      cod_fixed: nz(num(cell(row, 'Fixed COD Charge(Optional)')), 0),
      rvp_noqc: num(cell(row, 'RVP Without QC')),
      rvp_qc: num(cell(row, 'RVP With QC')),
      rto_pct: nz(num(cell(row, 'RTO (Forward %)')), 0),
    });
  });
  return { rows: out, badHeader: false, badPriceCells };
}

// Meta wins: the file was scoped at download time; the live wizard model is
// only a fallback for files whose __META__ sheet is missing or unreadable.
export function parseWorkbookToModel(buf, wizard) {
  const wb = XLSX.read(buf, { type: 'array' });
  const meta = readMeta(wb);
  const expected = meta
    ? meta.combos
    : buildTemplateModel(wizard).combos.map(({ sheetName, parent, accountCode, serviceId, kind, blended, lane }) => ({ sheetName, parent, accountCode, serviceId, kind, blended, lane }));
  const known = new Set(expected.map((c) => c.sheetName).concat('__META__'));
  const combos = [];
  const missingSheets = [];
  const badHeaderSheets = [];
  let badPriceCells = 0;
  expected.forEach((c) => {
    const ws = wb.Sheets[c.sheetName];
    if (!ws) { missingSheets.push(c.sheetName); return; }
    const parsed = parseComboSheet(ws);
    if (parsed.badHeader) { badHeaderSheets.push(c.sheetName); return; }
    badPriceCells += parsed.badPriceCells;
    combos.push({ parent: c.parent, accountCode: c.accountCode, serviceId: c.serviceId, kind: c.kind, blended: c.blended, lane: c.lane, rows: parsed.rows });
  });
  const unknownSheets = wb.SheetNames.filter((n) => !known.has(n));
  return {
    combos, missingSheets, badHeaderSheets, unknownSheets, badPriceCells,
    metaSlabTemplate: meta ? (meta.slabTemplate || null) : null,
  };
}
